import { Feature, getUid } from "ol";
import { asString } from "ol/color";
import KML from "ol/format/KML";
import CircleGeom from "ol/geom/Circle";
import GeometryCollection from "ol/geom/GeometryCollection";
import MultiPoint from "ol/geom/MultiPoint";
import Point from "ol/geom/Point";
import { fromCircle } from "ol/geom/Polygon";
import { get, transform } from "ol/proj";
import { Circle, Fill, Icon, Stroke, Style, Text } from "ol/style";
import { parse } from "ol/xml";
import getPolygonPattern from "./getPolygonPattern";
import { kmlStyle } from "./Styles";
const CIRCLE_GEOMETRY_CENTER = "circleGeometryCenter";
const CIRCLE_GEOMETRY_RADIUS = "circleGeometryRadius";
const EPSG_4326 = get("EPSG:4326");
const scaleForSize = (size) => {
  return 32 / Math.min(size[0], size[1]);
};
const applyTextStyleForIcon = (olIcon, olText) => {
  const size = olIcon.getSize() || [48, 48];
  const scale = olIcon.getScale() || 1;
  const anchor = olIcon.getAnchor() || [
    size[0] * scale / 2,
    size[1] * scale / 2
  ];
  const offset = [
    scale * (size[0] - anchor[0]) + 5,
    scale * (size[1] / 2 - anchor[1])
  ];
  olText.setOffsetX(offset[0]);
  olText.setOffsetY(offset[1]);
  olText.setTextAlign("left");
};
const getVertexCoord = (geom, start = true, index = 0) => {
  const coords = geom.getCoordinates();
  const len = coords.length - 1;
  return start ? coords[index] : coords[len - index];
};
const getLineIcon = (feature, icon, color, start = true) => {
  const geom = feature.getGeometry();
  const coordA = getVertexCoord(geom, start, 1);
  const coordB = getVertexCoord(geom, start);
  const dx = start ? coordA[0] - coordB[0] : coordB[0] - coordA[0];
  const dy = start ? coordA[1] - coordB[1] : coordB[1] - coordA[1];
  const rotation = Math.atan2(dy, dx);
  return new Style({
    geometry: (feat) => {
      const ge = feat.getGeometry();
      return new Point(getVertexCoord(ge, start));
    },
    image: new Icon({
      color,
      rotateWithView: true,
      rotation: -rotation,
      scale: icon.scale,
      size: icon.size,
      // ie 11
      src: icon.url
    }),
    zIndex: icon.zIndex
  });
};
const sanitizeFeature = (feature, doNotRevert32pxScaling = false) => {
  const geom = feature.getGeometry();
  let styles = feature.getStyleFunction();
  if (feature.get("maxZoom")) {
    feature.set("maxZoom", parseFloat(feature.get("maxZoom"), 10));
  }
  if (feature.get("minZoom")) {
    feature.set("minZoom", parseFloat(feature.get("minZoom"), 10));
  }
  const tmpStyles = styles(feature);
  const style = (Array.isArray(tmpStyles) ? tmpStyles[0] : tmpStyles).clone();
  let stroke = style.getStroke();
  if (feature.get("lineCap")) {
    stroke?.setLineCap(feature.get("lineCap"));
  }
  if (feature.get("lineJoin")) {
    stroke?.setLineJoin(feature.get("lineJoin"));
  }
  if (feature.get("lineDash")) {
    stroke?.setLineDash(
      feature.get("lineDash").split(",").map((l) => {
        return parseInt(l, 10);
      })
    );
  }
  if (feature.get("lineDashOffset")) {
    stroke?.setLineDashOffset(parseInt(feature.get("lineDashOffset"), 10));
  }
  if (feature.get("miterLimit")) {
    stroke?.setMiterLimit(parseInt(feature.get("miterLimit"), 10));
  }
  if (stroke && stroke.getWidth() === 0) {
    stroke = void 0;
  }
  if (feature.get("zIndex")) {
    style.setZIndex(parseInt(feature.get("zIndex"), 10));
  }
  if (style && (geom instanceof Point || geom instanceof MultiPoint)) {
    let image = style.getImage();
    let text = null;
    let fill = style.getFill();
    if (feature.get("name") && style.getText() && style.getText().getScale() !== 0) {
      if (image && image.getScale() === 0) {
        image = new Circle({
          fill: new Fill({ color: [0, 0, 0, 0] }),
          radius: 1,
          stroke: new Stroke({ color: [0, 0, 0, 0] })
        });
      }
      let name = feature.get("name");
      if (/\u200B/g.test(name)) {
        name = name.replace(/\u200B/g, "");
        feature.set("name", name);
      }
      const font = feature.get("textFont") || "normal 16px Helvetica";
      if (/\n/.test(name)) {
        const array = [];
        const split = name.split("\n");
        split.forEach((txt, idx) => {
          array.push(txt || "\u200B", txt ? font : "");
          if (idx < split.length - 1) {
            array.push("\n", "");
          }
        });
        name = array;
      } else {
        name = [name, font];
      }
      text = new Text({
        fill: style.getText().getFill(),
        font: font.replace(/bold/g, "normal"),
        // We manage bold in textArray
        // rotation unsupported by KML, taken instead from custom field.
        rotation: feature.get("textRotation") || 0,
        // stroke: style.getText().getStroke(),
        scale: style.getText().getScale(),
        // since ol 6.3.1 : https://github.com/openlayers/openlayers/pull/10613/files#diff-1883da8b57e690db7ea0c35ce53c880aR925
        // a default textstroke is added to mimic google earth.
        // it was not the case before, the stroke was always null. So to keep
        // the same behavior we don't copy the stroke style.
        // TODO : maybe we should use this functionnality in the futur.
        text: name
      });
      if (feature.get("textArray")) {
        try {
          const textArray = JSON.parse(feature.get("textArray"));
          text.setText(textArray);
        } catch (err) {
          console.error(
            "Error parsing textArray",
            feature.get("textArray"),
            err
          );
        }
      }
      if (feature.get("textStrokeColor") && feature.get("textStrokeWidth")) {
        text.setStroke(
          new Stroke({
            color: feature.get("textStrokeColor"),
            width: parseFloat(feature.get("textStrokeWidth"))
          })
        );
      }
      if (feature.get("textAlign")) {
        text.setTextAlign(feature.get("textAlign"));
      }
      if (feature.get("textOffsetX")) {
        text.setOffsetX(parseFloat(feature.get("textOffsetX")));
      }
      if (feature.get("textOffsetY")) {
        text.setOffsetY(parseFloat(feature.get("textOffsetY")));
      }
      if (feature.get("textBackgroundFillColor")) {
        text.setBackgroundFill(
          new Fill({
            color: feature.get("textBackgroundFillColor")
          })
        );
      }
      if (feature.get("textPadding")) {
        text.setPadding(
          feature.get("textPadding").split(",").map((n) => {
            return parseFloat(n);
          })
        );
      }
      if (image instanceof Icon) {
        applyTextStyleForIcon(image, text);
      }
    }
    if (image instanceof Icon) {
      image.setRotation(parseFloat(feature.get("iconRotation")) || 0);
      if (feature.get("iconScale")) {
        image.setScale(parseFloat(feature.get("iconScale")) || 0);
      } else if (!doNotRevert32pxScaling && image.getSize()) {
        const resizeScale = scaleForSize(image.getSize());
        image.setScale(image.getScaleArray()[0] / resizeScale);
      }
    }
    fill = void 0;
    stroke = void 0;
    styles = (feat, resolution) => {
      if (feat.get("pictureOptions")) {
        let pictureOptions = feat.get("pictureOptions");
        if (typeof pictureOptions === "string") {
          pictureOptions = JSON.parse(pictureOptions);
        }
        feat.set("pictureOptions", pictureOptions);
        if (pictureOptions.resolution) {
          image.setScale(
            pictureOptions.resolution / resolution * pictureOptions.defaultScale || 1
          );
        }
      }
      return new Style({
        fill,
        image,
        stroke,
        text,
        zIndex: style.getZIndex()
      });
    };
  }
  if (!(geom instanceof Point || geom instanceof MultiPoint || geom instanceof GeometryCollection)) {
    styles = [
      new Style({
        fill: style.getFill(),
        image: null,
        stroke,
        text: null,
        zIndex: style.getZIndex()
      })
    ];
    let fillPattern = feature.get("fillPattern");
    if (fillPattern) {
      fillPattern = JSON.parse(fillPattern);
      feature.set("fillPattern", fillPattern);
      if (!style.getFill()) {
        styles[0].setFill(new Fill());
      }
      const patternOrColor = fillPattern.empty ? [0, 0, 0, 0] : getPolygonPattern(fillPattern.id, fillPattern.color);
      styles[0].getFill().setColor(patternOrColor);
    }
    if (feature.get("lineStartIcon")) {
      styles.push(
        getLineIcon(
          feature,
          JSON.parse(feature.get("lineStartIcon")),
          stroke.getColor()
        )
      );
    }
    if (feature.get("lineEndIcon")) {
      styles.push(
        getLineIcon(
          feature,
          JSON.parse(feature.get("lineEndIcon")),
          stroke.getColor(),
          false
        )
      );
    }
  }
  feature.setStyle(styles);
};
const readFeatures = (kmlString, featureProjection, doNotRevert32pxScaling = false) => {
  const features = new KML().readFeatures(kmlString, {
    featureProjection
  });
  features.forEach((feature) => {
    const {
      [CIRCLE_GEOMETRY_CENTER]: circleGeometryCenter,
      [CIRCLE_GEOMETRY_RADIUS]: circleGeometryRadius
    } = feature?.getProperties() || {};
    if (circleGeometryCenter && circleGeometryRadius) {
      const circle = new CircleGeom(
        transform(
          JSON.parse(circleGeometryCenter),
          EPSG_4326,
          featureProjection || EPSG_4326
        ),
        parseFloat(circleGeometryRadius, 10)
      );
      circle.setProperties(feature.getGeometry().getProperties());
      feature.setGeometry(circle);
    }
    sanitizeFeature(feature, doNotRevert32pxScaling);
  });
  return features;
};
const writeFeatures = (layer, featureProjection, mapResolution) => {
  let featString;
  const { olLayer } = layer;
  const exportFeatures = [];
  [...olLayer.getSource().getFeatures()].sort((a, b) => {
    if (getUid(a) <= getUid(b)) {
      return -1;
    }
    return 1;
  }).forEach((feature) => {
    const clone = feature.clone();
    if (clone.getGeometry().getType() === "Circle") {
      const circleGeom = feature.getGeometry();
      clone.setGeometry(fromCircle(circleGeom, 100));
      clone.set(
        CIRCLE_GEOMETRY_CENTER,
        JSON.stringify(
          transform(circleGeom.getCenter(), featureProjection, EPSG_4326)
        )
      );
      clone.set(CIRCLE_GEOMETRY_RADIUS, circleGeom.getRadius());
    }
    clone.setId(feature.getId());
    clone.getGeometry().transform(featureProjection, EPSG_4326);
    Object.keys(feature.getProperties()).forEach((key) => {
      if (![
        CIRCLE_GEOMETRY_CENTER,
        CIRCLE_GEOMETRY_RADIUS,
        "description",
        "geometry",
        "name"
      ].includes(key)) {
        clone.unset(key, true);
      }
    });
    let styles;
    if (feature.getStyleFunction()) {
      styles = feature.getStyleFunction()(feature, mapResolution);
    } else if (olLayer && olLayer.getStyleFunction()) {
      styles = olLayer.getStyleFunction()(feature, mapResolution);
    }
    const mainStyle = styles[0] || styles;
    const newStyle = {
      fill: mainStyle.getFill(),
      image: mainStyle.getImage(),
      stroke: mainStyle.getStroke(),
      text: mainStyle.getText(),
      zIndex: mainStyle.getZIndex()
    };
    if (newStyle.zIndex) {
      clone.set("zIndex", newStyle.zIndex);
    }
    const text = newStyle.text?.getText();
    if (text) {
      let kmlText = text;
      if (Array.isArray(text)) {
        clone.set("textArray", JSON.stringify(text));
        kmlText = text.map((t, idx) => {
          return idx % 2 === 0 ? t : "";
        }).join("").replaceAll("\u200B", "");
      }
      if (kmlText) {
        if (/^(\s|\n)|(\n|\s)$/g.test(kmlText)) {
          clone.set("name", `\u200B${kmlText}\u200B`);
        } else {
          clone.set("name", kmlText);
        }
      }
    }
    if (newStyle.text?.getRotation()) {
      clone.set("textRotation", newStyle.text.getRotation());
    }
    if (newStyle.text?.getFont()) {
      clone.set("textFont", newStyle.text.getFont());
    }
    if (newStyle.text?.getTextAlign()) {
      clone.set("textAlign", newStyle.text.getTextAlign());
    }
    if (newStyle.text?.getOffsetX()) {
      clone.set("textOffsetX", newStyle.text.getOffsetX());
    }
    if (newStyle.text?.getOffsetY()) {
      clone.set("textOffsetY", newStyle.text.getOffsetY());
    }
    if (newStyle.text?.getStroke()) {
      if (newStyle.text.getStroke().getColor()) {
        clone.set(
          "textStrokeColor",
          asString(newStyle.text.getStroke().getColor())
        );
      }
      if (newStyle.text.getStroke().getWidth()) {
        clone.set("textStrokeWidth", newStyle.text.getStroke().getWidth());
      }
    }
    if (newStyle.text?.getBackgroundFill()) {
      clone.set(
        "textBackgroundFillColor",
        asString(newStyle.text.getBackgroundFill().getColor())
      );
    }
    if (newStyle.text?.getPadding()) {
      clone.set("textPadding", newStyle.text.getPadding().join());
    }
    if (newStyle.stroke?.getLineCap()) {
      clone.set("lineCap", newStyle.stroke.getLineCap());
    }
    if (newStyle.stroke?.getLineJoin()) {
      clone.set("lineJoin", newStyle.stroke.getLineJoin());
    }
    if (newStyle.stroke?.getLineDash()) {
      clone.set("lineDash", newStyle.stroke.getLineDash().join(","));
    }
    if (newStyle.stroke?.getLineDashOffset()) {
      clone.set("lineDashOffset", newStyle.stroke.getLineDashOffset());
    }
    if (newStyle.stroke?.getMiterLimit()) {
      clone.set("miterLimit", newStyle.stroke.getMiterLimit());
    }
    if (newStyle.image instanceof Circle) {
      newStyle.image = null;
    }
    if (newStyle.image) {
      const imgSource = newStyle.image.getSrc();
      if (!/(http(s?)):\/\//gi.test(imgSource)) {
        console.log(
          "Local image source not supported for KML export.Should use remote web server"
        );
      }
      if (newStyle.image.getRotation()) {
        clone.set("iconRotation", newStyle.image.getRotation());
      }
      if (newStyle.image.getScale()) {
        clone.set("iconScale", newStyle.image.getScale());
      }
      if (feature.get("pictureOptions")) {
        clone.set(
          "pictureOptions",
          JSON.stringify(feature.get("pictureOptions"))
        );
      }
    }
    if (feature.get("fillPattern")) {
      clone.set("fillPattern", JSON.stringify(feature.get("fillPattern")));
      newStyle.fill = null;
    }
    if (feature.get("maxZoom")) {
      clone.set("maxZoom", parseFloat(feature.get("maxZoom"), 10));
    }
    if (feature.get("minZoom")) {
      clone.set("minZoom", parseFloat(feature.get("minZoom"), 10));
    }
    if (newStyle.text && !newStyle.image) {
      newStyle.image = new Icon({
        scale: 0,
        src: "noimage"
      });
    }
    const extraLineStyles = Array.isArray(styles) && styles.slice(1) || [];
    extraLineStyles.forEach((extraLineStyle) => {
      if (extraLineStyle && extraLineStyle.getImage() instanceof Icon && extraLineStyle.getGeometry()) {
        const coord = extraLineStyle.getGeometry()(feature).getCoordinates();
        const startCoord = feature.getGeometry().getFirstCoordinate();
        if (coord[0] === startCoord[0] && coord[1] === startCoord[1]) {
          clone.set(
            "lineStartIcon",
            JSON.stringify({
              scale: extraLineStyle.getImage().getScale(),
              size: extraLineStyle.getImage().getSize(),
              url: extraLineStyle.getImage().getSrc(),
              zIndex: extraLineStyle.getZIndex()
            })
          );
        } else {
          clone.set(
            "lineEndIcon",
            JSON.stringify({
              scale: extraLineStyle.getImage().getScale(),
              size: extraLineStyle.getImage().getSize(),
              url: extraLineStyle.getImage().getSrc(),
              zIndex: extraLineStyle.getZIndex()
            })
          );
        }
      }
    });
    const olStyle = new Style(newStyle);
    clone.setStyle(olStyle);
    if (!(clone.getGeometry() instanceof Point && olStyle.getText() && !olStyle.getText().getText())) {
      exportFeatures.push(clone);
    }
  });
  if (exportFeatures.length > 0) {
    if (exportFeatures.length === 1) {
      exportFeatures.push(new Feature());
    }
    featString = new KML({
      defaultStyle: [kmlStyle],
      extractStyles: true
    }).writeFeatures(exportFeatures);
    featString = featString.replace(
      /<Icon>\s*<href>noimage<\/href>\s*<\/Icon>/g,
      ""
    );
    featString = featString.replace(/<Placemark\/>/g, "");
    if (layer.name) {
      featString = featString.replace(
        /<Document>/,
        `<Document><name>${layer.name}</name>`
      );
    }
  }
  return featString;
};
const removeDocumentCamera = (kmlString) => {
  const kmlDoc = parse(kmlString);
  const oldCameraNode = kmlDoc.getElementsByTagName("Camera")[0];
  if (oldCameraNode) {
    oldCameraNode.remove();
  }
  return new XMLSerializer().serializeToString(kmlDoc);
};
const writeDocumentCamera = (kmlString, cameraAttributes) => {
  const kmlDoc = parse(removeDocumentCamera(kmlString));
  if (cameraAttributes) {
    const cameraNode = kmlDoc.createElement("Camera");
    Object.keys(cameraAttributes).forEach((key) => {
      const cameraAttribute = kmlDoc.createElement(
        `${key.charAt(0).toUpperCase() + key.slice(1)}`
      );
      cameraAttribute.innerHTML = cameraAttributes[key];
      cameraNode.appendChild(cameraAttribute);
    });
    const documentNode = kmlDoc.getElementsByTagName("Document")[0];
    documentNode.appendChild(cameraNode);
  }
  return new XMLSerializer().serializeToString(kmlDoc);
};
export default {
  readFeatures,
  removeDocumentCamera,
  writeDocumentCamera,
  writeFeatures
};
